import { UserSubscriptionDetails } from '@anm/api/modules/user';
import TitleBlock from '@anm/components/TitleBlock';
import CurrentPlanSwitcher from '@anm/components/subscription/current-plan/CurrentPlanSwitcher';
import useSwitchState from '@anm/hooks/useSwitchState';
import { FC } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import UpgradeUserSubscription from '../../../components/pricing/UpgradeUserSubscription';
import useSubscriptionList from '../../../hooks/subscription/useSubscriptionList';
import { subscriptionSelectors } from '../../../store/modules/subscription';
import { userSelectors } from '../../../store/modules/user';
import SuccessUpgradeAlert from '../../pricing/alerts/SuccessUpgradeAlert';
import CancelUserSubscription from '../CancelUserSubscription';

import useAppSumoRedeemHandler from './hooks/useAppSumoRedeemHandler';
import useAppSumoUpgradeHandler from './hooks/useAppSumoUpgradeHandler';
import useCurrentPlanPending from './hooks/useCurrentPlanPending';
import useUpgradeUserSubscriptionModalControls from './hooks/useUpgradeUserSubscriptionModalControls';

export type UserCurrentPlanProps = {};

const UserCurrentPlan: FC<UserCurrentPlanProps> = () => {
  const { fetchSubscriptionList } = useSubscriptionList();
  const [handleRedeem, appSumoSubmitError] = useAppSumoRedeemHandler();
  const isPending = useCurrentPlanPending();
  const details = useSelector(userSelectors.selectWaveSubscription, shallowEqual);
  const isFreeUser = useSelector(userSelectors.selectIsUserFree);
  const [upgradeAppSumoState, { upgrade: handleConfirmAppSumoUpgrade }] = useAppSumoUpgradeHandler();

  const nextInvoice = useSelector(subscriptionSelectors.selectCardNextInvoice, shallowEqual);
  const hasTeamAccess = useSelector(userSelectors.selectTeamAccess);
  const maxTeamUsers = useSelector(userSelectors.selectMaxTeamUsers);
  const hasUserCard = !!useSelector(subscriptionSelectors.selectCard, shallowEqual);

  const nextInvoiceDate = useSelector(subscriptionSelectors.selectCardNextInvoiceDate, shallowEqual);

  const [isUpgradeModalOpen, openUpgradeModal, closeUpgradeModal] = useUpgradeUserSubscriptionModalControls();

  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useSwitchState();
  const subscriptionChange = useSelector(subscriptionSelectors.selectSubscriptionChange);

  const { name, displayName, annualPrice, monthlyPrice, billingPeriod, expirationDate } =
    details || ({} as UserSubscriptionDetails);

  const displayNameLocal = hasTeamAccess ? `${displayName} ${maxTeamUsers}` : displayName;

  return (
    <div onMouseEnter={fetchSubscriptionList}>
      <TitleBlock size={'medium'} title="Current plan" />
      <SuccessUpgradeAlert subscriptionName={subscriptionChange?.planName || name} />
      {isCancelModalOpen && <CancelUserSubscription planName={displayNameLocal} onModalClose={closeCancelModal} />}
      {isUpgradeModalOpen && <UpgradeUserSubscription subscriptionName={name} onModalClose={closeUpgradeModal} />}
      <CurrentPlanSwitcher
        isFreeUser={isFreeUser}
        period={billingPeriod}
        planName={displayNameLocal}
        isPending={isPending}
        nextInvoice={nextInvoice}
        nextInvoiceDate={nextInvoiceDate}
        annualPrice={annualPrice}
        monthlyPrice={monthlyPrice}
        upgradeRoute="pricing"
        expirationDate={expirationDate}
        subscriptionPlan={name}
        hasUserCard={hasUserCard}
        appSumoSubmitError={appSumoSubmitError || upgradeAppSumoState.error}
        onCancel={openCancelModal}
        onRedeem={handleRedeem}
        onPeriodSwitch={openUpgradeModal}
        onConfirmAppSumoUpgrade={handleConfirmAppSumoUpgrade}
      />
    </div>
  );
};

export default UserCurrentPlan;
